/**
 * NexusSoundEngine – Web Audio API based synthesizer
 * Triple-A+ quality procedural sounds – no external files needed.
 * Every sound is generated in real-time using oscillators, filters, and envelopes.
 */

let ctx = null;
let masterGain = null;
let enabled = true;

function getCtx() {
  if (!ctx) {
    ctx = new (window.AudioContext || window.webkitAudioContext)();
    masterGain = ctx.createGain();
    masterGain.gain.value = 0.55;
    masterGain.connect(ctx.destination);
  }
  if (ctx.state === "suspended") ctx.resume();
  return { ctx, master: masterGain };
}

export function setSoundEnabled(val) {
  enabled = val;
  if (masterGain) masterGain.gain.value = val ? 0.55 : 0;
}

/* ─── Low-level helpers ──────────────────────────────────────────────────── */
function createOsc(ctx, type, freq) {
  const o = ctx.createOscillator();
  o.type = type;
  o.frequency.value = freq;
  return o;
}

function createEnv(ctx, master, attack = 0.01, decay = 0.1, sustain = 0.3, release = 0.3, peak = 0.8) {
  const g = ctx.createGain();
  const now = ctx.currentTime;
  g.gain.setValueAtTime(0, now);
  g.gain.linearRampToValueAtTime(peak, now + attack);
  g.gain.linearRampToValueAtTime(sustain * peak, now + attack + decay);
  g.gain.setValueAtTime(sustain * peak, now + attack + decay);
  g.gain.linearRampToValueAtTime(0, now + attack + decay + release);
  g.connect(master);
  return { g, endTime: now + attack + decay + release };
}

function createFilter(ctx, type, freq, q = 1) {
  const f = ctx.createBiquadFilter();
  f.type = type;
  f.frequency.value = freq;
  f.Q.value = q;
  f.connect(ctx.destination);
  return f;
}

function playNote(freq, type = "sine", duration = 0.3, vol = 0.5, attack = 0.01, release = 0.2) {
  if (!enabled) return;
  try {
    const { ctx: c, master } = getCtx();
    const osc = createOsc(c, type, freq);
    const g   = c.createGain();
    const now = c.currentTime;
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(vol, now + attack);
    g.gain.exponentialRampToValueAtTime(0.001, now + duration);
    osc.connect(g);
    g.connect(master);
    osc.start(now);
    osc.stop(now + duration + 0.05);
  } catch {}
}

/* ─── Reverb (convolver) ─────────────────────────────────────────────────── */
function createReverb(ctx, master, decay = 0.8) {
  const conv = ctx.createConvolver();
  const len  = ctx.sampleRate * decay;
  const buf  = ctx.createBuffer(2, len, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 2);
  }
  conv.buffer = buf;
  conv.connect(master);
  return conv;
}

/* ─── Sound effects ──────────────────────────────────────────────────────── */

/** Subtle UI click – crisp, satisfying */
export function sfxClick() {
  if (!enabled) return;
  try {
    const { ctx: c, master } = getCtx();
    const osc = createOsc(c, "triangle", 900);
    const g   = c.createGain();
    const now = c.currentTime;
    g.gain.setValueAtTime(0.18, now);
    g.gain.exponentialRampToValueAtTime(0.001, now + 0.06);
    osc.connect(g); g.connect(master);
    osc.start(now); osc.stop(now + 0.08);
  } catch {}
}

/** Navigation tab switch – smooth whoosh */
export function sfxNav() {
  if (!enabled) return;
  try {
    const { ctx: c, master } = getCtx();
    const osc = createOsc(c, "sine", 440);
    const now = c.currentTime;
    osc.frequency.setValueAtTime(300, now);
    osc.frequency.linearRampToValueAtTime(600, now + 0.12);
    const g = c.createGain();
    g.gain.setValueAtTime(0.12, now);
    g.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
    osc.connect(g); g.connect(master);
    osc.start(now); osc.stop(now + 0.18);
  } catch {}
}

/** Quiz correct answer – triumphant mini-chord */
export function sfxCorrect() {
  if (!enabled) return;
  const notes = [523.25, 659.25, 783.99]; // C5 E5 G5
  notes.forEach((freq, i) => {
    setTimeout(() => playNote(freq, "triangle", 0.45, 0.35, 0.01, 0.4), i * 60);
  });
}

/** Quiz wrong answer – dissonant dip */
export function sfxWrong() {
  if (!enabled) return;
  try {
    const { ctx: c, master } = getCtx();
    const now = c.currentTime;
    [220, 207].forEach((f, i) => {
      const o = createOsc(c, "sawtooth", f);
      const g = c.createGain();
      const filt = c.createBiquadFilter();
      filt.type = "lowpass"; filt.frequency.value = 600;
      o.connect(filt); filt.connect(g); g.connect(master);
      g.gain.setValueAtTime(0, now + i * 0.05);
      g.gain.linearRampToValueAtTime(0.2, now + i * 0.05 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
      o.start(now + i * 0.05); o.stop(now + 0.45);
    });
  } catch {}
}

/** XP/Coins earned – ascending sparkle cascade */
export function sfxReward() {
  if (!enabled) return;
  const freqs = [523, 659, 784, 1047, 1319];
  freqs.forEach((f, i) => {
    setTimeout(() => playNote(f, "sine", 0.3, 0.3, 0.005, 0.28), i * 80);
  });
}

/** Level up – epic ascending fanfare */
export function sfxLevelUp() {
  if (!enabled) return;
  const seq = [
    { f: 392, t: 0,   d: 0.2  },
    { f: 523, t: 150, d: 0.2  },
    { f: 659, t: 300, d: 0.2  },
    { f: 784, t: 450, d: 0.35 },
    { f: 1047,t: 600, d: 0.5  },
  ];
  seq.forEach(({ f, t, d }) => {
    setTimeout(() => playNote(f, "triangle", d, 0.4, 0.01, d * 0.8), t);
  });
}

/** Achievement unlock – mystical chime */
export function sfxAchievement() {
  if (!enabled) return;
  const { ctx: c, master } = getCtx();
  const harmonics = [1047, 1319, 1568, 2093];
  harmonics.forEach((f, i) => {
    setTimeout(() => {
      const now = c.currentTime;
      const o = createOsc(c, "sine", f);
      const g = c.createGain();
      g.gain.setValueAtTime(0, now);
      g.gain.linearRampToValueAtTime(0.22, now + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, now + 0.8);
      o.connect(g); g.connect(master);
      o.start(now); o.stop(now + 0.85);
    }, i * 90);
  });
}

/** Notification – soft ping */
export function sfxNotification() {
  if (!enabled) return;
  playNote(880, "sine", 0.2, 0.2, 0.005, 0.18);
  setTimeout(() => playNote(1108, "sine", 0.25, 0.15, 0.005, 0.22), 80);
}

/** Login success – energetic rise */
export function sfxLoginSuccess() {
  if (!enabled) return;
  const { ctx: c, master } = getCtx();
  const now = c.currentTime;
  // Sweep
  const sw = createOsc(c, "sawtooth", 200);
  sw.frequency.linearRampToValueAtTime(800, now + 0.4);
  const sgf = c.createBiquadFilter();
  sgf.type = "lowpass"; sgf.frequency.setValueAtTime(400, now); sgf.frequency.linearRampToValueAtTime(2000, now + 0.4);
  const sg = c.createGain();
  sg.gain.setValueAtTime(0.12, now); sg.gain.linearRampToValueAtTime(0, now + 0.4);
  sw.connect(sgf); sgf.connect(sg); sg.connect(master);
  sw.start(now); sw.stop(now + 0.45);
  // Chord hit
  [523, 659, 784].forEach((f, i) => setTimeout(() => playNote(f, "triangle", 0.5, 0.3, 0.01, 0.45), 400 + i * 50));
}

/** Shop purchase – satisfying cash register feel */
export function sfxPurchase() {
  if (!enabled) return;
  playNote(1319, "sine",     0.12, 0.3, 0.005, 0.1);
  setTimeout(() => playNote(1760, "triangle", 0.3,  0.35, 0.005, 0.27), 100);
}

/** Coin burn – intense fire crackle */
export function sfxCoinBurn(intensity = 1) {
  if (!enabled) return;
  try {
    const { ctx: c, master } = getCtx();
    const now = c.currentTime;
    const duration = 0.8 * intensity;
    // White noise crackle
    const bufLen = c.sampleRate * duration;
    const buf = c.createBuffer(1, bufLen, c.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < bufLen; i++) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / bufLen, 0.3);
    const noise = c.createBufferSource();
    noise.buffer = buf;
    const nFilt = c.createBiquadFilter();
    nFilt.type = "bandpass"; nFilt.frequency.value = 2000 + intensity * 800; nFilt.Q.value = 0.8;
    const nGain = c.createGain();
    nGain.gain.setValueAtTime(0.08 * intensity, now); nGain.gain.exponentialRampToValueAtTime(0.001, now + duration);
    noise.connect(nFilt); nFilt.connect(nGain); nGain.connect(master);
    noise.start(now); noise.stop(now + duration);
    // Sub rumble
    const sub = createOsc(c, "sawtooth", 80 - intensity * 10);
    const subG = c.createGain();
    subG.gain.setValueAtTime(0.15, now); subG.gain.exponentialRampToValueAtTime(0.001, now + duration * 0.6);
    sub.connect(subG); subG.connect(master);
    sub.start(now); sub.stop(now + duration);
    // High freq sizzle
    if (intensity > 1) {
      const siz = createOsc(c, "sine", 4000);
      const sizG = c.createGain();
      sizG.gain.setValueAtTime(0.1, now + 0.1); sizG.gain.exponentialRampToValueAtTime(0.001, now + duration * 0.5);
      siz.connect(sizG); sizG.connect(master);
      siz.start(now + 0.1); siz.stop(now + duration);
    }
  } catch {}
}

/** Launch animation ambient sweep */
export function sfxLaunch() {
  if (!enabled) return;
  try {
    const { ctx: c, master } = getCtx();
    const now = c.currentTime;
    // Pad chord
    const pads = [130.81, 164.81, 196, 261.63]; // C3 E3 G3 C4
    pads.forEach((f, i) => {
      const o = createOsc(c, "sine", f);
      const g = c.createGain();
      g.gain.setValueAtTime(0, now + i * 0.15);
      g.gain.linearRampToValueAtTime(0.08, now + i * 0.15 + 0.3);
      g.gain.linearRampToValueAtTime(0, now + 3.5);
      o.connect(g); g.connect(master);
      o.start(now); o.stop(now + 3.6);
    });
    // Rising sweep for energy
    const sweep = createOsc(c, "sawtooth", 100);
    sweep.frequency.linearRampToValueAtTime(1200, now + 2.5);
    const sweepFilt = c.createBiquadFilter();
    sweepFilt.type = "lowpass"; sweepFilt.frequency.setValueAtTime(300, now); sweepFilt.frequency.linearRampToValueAtTime(3000, now + 2.5);
    const sweepGain = c.createGain();
    sweepGain.gain.setValueAtTime(0, now); sweepGain.gain.linearRampToValueAtTime(0.06, now + 0.5); sweepGain.gain.linearRampToValueAtTime(0, now + 2.8);
    sweep.connect(sweepFilt); sweepFilt.connect(sweepGain); sweepGain.connect(master);
    sweep.start(now); sweep.stop(now + 3);
    // Final hit
    setTimeout(() => {
      [523, 659, 784, 1047].forEach((f, i) => setTimeout(() => playNote(f, "triangle", 0.6, 0.3, 0.01, 0.55), i * 50));
    }, 2500);
  } catch {}
}
